// src/pages/Products.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Tabs,
    Tab,
    Card,
    CardContent,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Avatar,
    Alert,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    CardActions,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    FormHelperText
} from '@mui/material';
import {
    Delete,
    Category,
    Search,
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Storage,
    PictureAsPdf,
    TableChart, 
    GetApp,
    Print,
    AttachMoney,
    TrendingUp,
    TrendingDown,
    Warning,
    CheckCircle,
    Store,
    Inventory2,
    LocalShipping,
    CalendarMonth,
    ViewModule,
    ViewList,
    Description,
    LocalOffer,
    Info,
    Cancel,
    MoneyOff,
    Assessment,
    Inventory,
    Person,
    Update,
    MoreVert,
    Scale
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APICategoriesNames,
    APIProductsDelete,
    APIProductsList,
    APIProductsStore,
    APIProductsUpdate,
    APISupplierList,
    APIProductsStats,
    APIProductsExportExcel,
    APIProductsExportPDF,
    APIProductsPrint,
    APIUnitName
} from '../../apiServices/APIs';
import { formatCreatedAtDate, formatNumberWithComma, getCategoriesNameById, getStatusById, recordsPerPage, statusData, recordsCount } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Products = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedProductForAction, setSelectedProductForAction] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
        categoriesData: [],
        suppliersData: [],
        unitsData: [],
        productsStats: {
            total_products: 0,
            active_products: 0,
            inactive_products: 0,
            average_price: 0,
            total_stock: 0,
            low_stock_count: 0,
            out_of_stock_count: 0,
            total_inventory_value: 0,
            total_profit_potential: 0,
            average_rating: 0
        }
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('list');
    const [viewActiveTab, setViewActiveTab] = useState(0);
    
    // Search input states for autocomplete
    const [unitSearchInput, setUnitSearchInput] = useState('');
    const [categorySearchInput, setCategorySearchInput] = useState('');
    const [supplierSearchInput, setSupplierSearchInput] = useState('');

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        rating: '',
        cost: '',
        selling: '',
        status: '',
        category: '',
        description: '',
        stock: '',
        payment: '',
        supplier: '',
        unit_id: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        cost: '', loan_filter: '',
        selling: '',
        status: '',
        category: '',
        unit_id: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        rating: false,
        cost: false,
        selling: false,
        status: false,
        category: false,
        description: false,
        stock: false,
        unit_id: false
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    
    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        fetchProductsStats();

        return () => {
            isMounted.current = false;
        };
    }, [showToast]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const [productsResult, categoriesResult, suppliersResult, unitsResult] = await Promise.all([
                apiService.list(userBranchId, APIProductsList),
                apiService.list(userBranchId, APICategoriesNames),
                apiService.list(userBranchId, APISupplierList),
                apiService.list(userBranchId, APIUnitName)
            ]);
            
            if (productsResult.success) {
                const products = Array.isArray(productsResult.data) ? productsResult.data : [];
                setPageState(prev => ({ ...prev, pageData: products }));
            } else {
                setError(productsResult.message);
            }
            
            if (categoriesResult.success) {
                const categories = Array.isArray(categoriesResult.data) ? categoriesResult.data.filter(cat => cat.status === 1) : [];
                setPageState(prev => ({ ...prev, categoriesData: categories }));
            }
            
            if (suppliersResult.success) {
                const suppliers = Array.isArray(suppliersResult.data) ? suppliersResult.data : [];
                setPageState(prev => ({ ...prev, suppliersData: suppliers }));
            }
            
            if (unitsResult.success) {
                const units = Array.isArray(unitsResult.data) ? unitsResult.data : [];
                setPageState(prev => ({ ...prev, unitsData: units }));
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProductsStats = async () => {
        setStatsLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIProductsStats);
            if (result.success && result.data) {
                setPageState(prev => ({ ...prev, productsStats: result.data }));
            } else {
                calculateLocalStats();
            }
        } catch (error) {
            calculateLocalStats();
        } finally {
            setStatsLoading(false);
        }
    };

    const calculateLocalStats = () => {
        const products = pageState.pageData;
        const totalProducts = products.length;
        const activeProducts = products.filter(p => p.status === 1).length;
        const inactiveProducts = products.filter(p => p.status !== 1).length;
        const totalStock = products.reduce((sum, product) => sum + (product.stock || 0), 0);
        const lowStockCount = products.filter(p => p.stock > 0 && p.stock <= 10).length;
        const outOfStockCount = products.filter(p => p.stock === 0).length;
        const totalInventoryValue = products.reduce((sum, product) => sum + (product.stock * product.selling), 0);
        const totalProfitPotential = products.reduce((sum, product) => sum + (product.stock * (product.selling - product.cost)), 0);
        const averagePrice = products.length > 0 ? Math.round(products.reduce((sum, product) => sum + product.selling, 0) / products.length) : 0;
        const averageRating = products.length > 0 ? (products.reduce((sum, product) => sum + (product.rating || 0), 0) / products.length).toFixed(1) : 0;
        setPageState(prev => ({ 
            ...prev, 
            productsStats: { 
                ...prev.productsStats, 
                total_products: totalProducts, 
                active_products: activeProducts, 
                inactive_products: inactiveProducts, 
                total_stock: totalStock, 
                low_stock_count: lowStockCount, 
                out_of_stock_count: outOfStockCount, 
                total_inventory_value: totalInventoryValue, 
                total_profit_potential: totalProfitPotential, 
                average_price: averagePrice, 
                average_rating: averageRating 
            } 
        }));
    };

    useEffect(() => {
        if (pageState.pageData.length > 0 && !statsLoading) {
            calculateLocalStats();
        }
    }, [pageState.pageData]);

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) { 
            showToast("Permission Denied", "You don't have permission to print product details", "error"); 
            return; 
        }
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APIProductsPrint, formDataToSend);
            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) { 
            showToast("Error", "Failed to generate PDF file", "error"); 
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) { 
            handleExportClose(); 
            return; 
        }
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            // Add filters
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.category) formDataToSend.append('category', filters.category);
            if (filters.status) formDataToSend.append('status', filters.status);
            if (filters.unit_id) formDataToSend.append('unit_id', filters.unit_id);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            // Add loan filter
            if (filters.loan_filter) formDataToSend.append('loan_filter', filters.loan_filter);
            
            const result = await apiService.exportToExcel(APIProductsExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) { 
            showToast("Error", error.message || "Failed to export Excel file", "error"); 
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) { 
            handleExportClose(); 
            return; 
        }
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            // Add filters
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.category) formDataToSend.append('category', filters.category);
            if (filters.status) formDataToSend.append('status', filters.status);
            if (filters.unit_id) formDataToSend.append('unit_id', filters.unit_id);
            
            // Add loan filter
            if (filters.loan_filter) formDataToSend.append('loan_filter', filters.loan_filter);
            
            const result = await apiService.exportToPDF(APIProductsExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) { 
            showToast("Error", error.message || "Failed to export PDF file", "error"); 
        }
    };

    // Filter based on search term and filters
    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const unitName = pageState.unitsData.find(u => u.id === record.unit_id)?.name || '';
            
            const matchesSearch = searchTerm === '' || 
                record.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                record.selling?.toString().includes(searchTerm) || 
                record.cost?.toString().includes(searchTerm) ||
                unitName.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesLoanFilter = !filters.loan_filter || 
                (filters.loan_filter === 'with_loans' ? record.loan !== null : record.loan === null);

            const matchesName = !filters.name || 
                record.name?.toLowerCase().includes(filters.name.toLowerCase());
            
            const matchesSelling = !filters.selling || 
                record.selling?.toString().includes(filters.selling);
            
            const matchesCost = !filters.cost || 
                record.cost?.toString().includes(filters.cost);
            
            const matchesStatus = !filters.status || 
                String(record.status) === String(filters.status);
            
            const matchesCategory = !filters.category || 
                String(record.category) === String(filters.category);
            
            const matchesUnit = !filters.unit_id || 
                String(record.unit_id) === String(filters.unit_id);

            let matchesDate = true;
            if (filters.dateFrom) { 
                const recordDate = new Date(record.created_at).toISOString().split('T')[0]; 
                const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0]; 
                matchesDate = matchesDate && recordDate >= filterFromDate; 
            }
            if (filters.dateTo) { 
                const recordDate = new Date(record.created_at).toISOString().split('T')[0]; 
                const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0]; 
                matchesDate = matchesDate && recordDate <= filterToDate; 
            }
            return matchesSearch && matchesLoanFilter && matchesName && matchesSelling && matchesCost && matchesDate && matchesStatus && matchesCategory && matchesUnit;
        });
    }, [pageState.pageData, searchTerm, filters, pageState.unitsData]);

    const calculateFilteredTotals = () => {
        const totalCost = filteredData.reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0);
        const totalSelling = filteredData.reduce((sum, p) => sum + (parseFloat(p.selling) || 0), 0);
        const totalProfit = filteredData.reduce((sum, p) => sum + ((parseFloat(p.selling) || 0) - (parseFloat(p.cost) || 0)), 0);
        const totalStock = filteredData.reduce((sum, p) => sum + (parseInt(p.stock) || 0), 0);
        const totalInventoryValue = filteredData.reduce((sum, p) => sum + ((parseInt(p.stock) || 0) * (parseFloat(p.selling) || 0)), 0);
        const activeProducts = filteredData.filter(p => p.status == 1).length;
        const inactiveProducts = filteredData.filter(p => p.status != 1).length;
        const inStockProducts = filteredData.filter(p => p.stock > 0).length;
        const outOfStockProducts = filteredData.filter(p => p.stock === 0).length;
        const lowStockProducts = filteredData.filter(p => p.stock > 0 && p.stock <= 10).length;
        const uniqueCategories = new Set(filteredData.map(p => p.category_name)).size;
        const avgPrice = filteredData.length > 0 ? Math.round(filteredData.reduce((sum, p) => sum + (parseFloat(p.selling) || 0), 0) / filteredData.length) : 0;
        const avgCost = filteredData.length > 0 ? Math.round(filteredData.reduce((sum, p) => sum + (parseFloat(p.cost) || 0), 0) / filteredData.length) : 0;

        return {
            totalCost,
            totalSelling,
            totalProfit,
            totalStock,
            totalInventoryValue,
            activeProducts,
            inactiveProducts,
            inStockProducts,
            outOfStockProducts,
            lowStockProducts,
            uniqueCategories,
            avgPrice,
            avgCost,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { 
        setRowsPerPage(parseInt(event.target.value, 10)); 
        setPage(0); 
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
    };

    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) { 
            showToast("Permission Denied", "You don't have permission to add products", "error"); 
            return; 
        }
        setPostStatus("ADD");
        setFormData({ 
            name: '', 
            rating: '', 
            cost: '', 
            selling: '', 
            status: '', 
            category: '', 
            description: '', 
            stock: '', 
            payment: '', 
            supplier: '',
            unit_id: '' 
        });
        setUnitSearchInput('');
        setCategorySearchInput('');
        setSupplierSearchInput('');
        setActiveTab(0);
        setErrors({ 
            rating: false, 
            cost: false, 
            selling: false, 
            status: false, 
            category: false, 
            description: false, 
            stock: false,
            unit_id: false 
        });
        setOpenAdd(true);
    };
    
    const handleCloseAdd = () => { 
        setOpenAdd(false); 
        setActiveTab(0);
        setUnitSearchInput('');
        setCategorySearchInput('');
        setSupplierSearchInput('');
    };

    const handleOpenView = (record) => { 
        setSelectedRecord(record); 
        setViewActiveTab(0); 
        setOpenView(true); 
        handleActionMenuClose();
    };
    const handleCloseView = () => { setOpenView(false); setViewActiveTab(0); };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) { 
            showToast("Permission Denied", "You don't have permission to edit products", "error"); 
            return; 
        }
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        // Resolve matching category ID
        const resolvedCategory = pageState.categoriesData.find(
            cat => String(cat.id) === String(record.category) || cat.name === record.category_name || cat.name === record.category
        )?.id || record.category || '';

        // Resolve matching supplier ID
        const resolvedSupplier = pageState.suppliersData.find(
            sup => String(sup.id) === String(record.supplier) || sup.name === record.supplier_name || sup.name === record.supplier
        )?.id || record.supplier || '';

        // Resolve matching unit ID
        const resolvedUnit = pageState.unitsData.find(
            unit => String(unit.id) === String(record.unit_id) || unit.name === record.unit_name
        )?.id || record.unit_id || '';

        // Set search inputs
        const selectedUnit = pageState.unitsData.find(u => u.id === resolvedUnit);
        const selectedCategory = pageState.categoriesData.find(c => c.id === resolvedCategory);
        const selectedSupplier = pageState.suppliersData.find(s => s.id === resolvedSupplier);

        setUnitSearchInput(selectedUnit ? selectedUnit.name : '');
        setCategorySearchInput(selectedCategory ? selectedCategory.name : '');
        setSupplierSearchInput(selectedSupplier ? selectedSupplier.name : '');

        setFormData({ 
            name: record.name || '', 
            cost: record.cost || '', 
            description: record.description || '', 
            rating: record.rating || '', 
            selling: record.selling || '', 
            status: String(record.status || ''), 
            category: resolvedCategory, 
            stock: record.stock || '', 
            payment: record.payment || '', 
            supplier: resolvedSupplier,
            unit_id: resolvedUnit
        });
        setActiveTab(0);
        setOpenAdd(true);
        handleActionMenuClose();
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) { 
            showToast("Permission Denied", "You don't have permission to delete products", "error"); 
            return; 
        }
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
        handleActionMenuClose();
    };
    const handleCloseDelete = () => setOpenDelete(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: false }));
        if (name === 'cost' || name === 'selling' || name === 'stock' || name === 'rating') {
            if (value === '' || /^\d*\.?\d*$/.test(value)) { 
                setFormData(prev => ({ ...prev, [name]: value })); 
            } else { 
                setErrors(prev => ({ ...prev, [name]: true })); 
                showToast("Input Error", "Please enter valid numbers only", "error"); 
                return; 
            }
        } else { 
            setFormData(prev => ({ ...prev, [name]: value })); 
        }
    };

    // Handler for unit selection
    const handleUnitSelect = (event, selectedUnit) => {
        setFormData(prev => ({
            ...prev,
            unit_id: selectedUnit ? selectedUnit.id : ''
        }));
    };

    // Handler for category selection
    const handleCategorySelect = (event, selectedCategory) => {
        setFormData(prev => ({
            ...prev,
            category: selectedCategory ? selectedCategory.id : ''
        }));
    };

    // Handler for supplier selection
    const handleSupplierSelect = (event, selectedSupplier) => {
        setFormData(prev => ({
            ...prev,
            supplier: selectedSupplier ? selectedSupplier.id : ''
        }));
    };

    const handleFilterChange = (e) => { 
        const { name, value } = e.target; 
        setFilters(prev => ({ ...prev, [name]: value })); 
    };

    const handleFilterSelectChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => { 
        setFilters({ 
            name: '', 
            cost: '', 
            selling: '', 
            status: '', 
            category: '', 
            unit_id: '',
            dateFrom: '', 
            dateTo: '' 
        }); 
        setSearchTerm(''); 
        handleCloseOptions(); 
    };
    
    const applyFilters = () => handleCloseOptions();

    const handleTabChange = (event, newValue) => setActiveTab(newValue);
    const handleNextTab = () => { if (activeTab < 3) setActiveTab(activeTab + 1); };
    const handlePrevTab = () => { if (activeTab > 0) setActiveTab(activeTab - 1); };

    const validateForm = () => {
        if (!formData.name?.trim()) { showToast("Validation Error", "Product name is required", "warning"); return false; }
        if (!formData.description?.trim()) { showToast("Validation Error", "Description is required", "warning"); return false; }
        if (!String(formData.status || "").trim()) { showToast("Validation Error", "Status is required", "warning"); return false; }
        if (!String(formData.cost || "").trim()) { showToast("Validation Error", "Cost price is required", "warning"); return false; }
        if (!String(formData.selling || "").trim()) { showToast("Validation Error", "Selling price is required", "warning"); return false; }
        if (!String(formData.stock || "").trim()) { showToast("Validation Error", "Stock is required", "warning"); return false; }
        if (!String(formData.rating || "").trim()) { showToast("Validation Error", "Rating is required", "warning"); return false; }
        if (!String(formData.payment || "").trim()) { showToast("Validation Error", "Payment method is required", "warning"); return false; }
        if (!String(formData.supplier || "").trim()) { showToast("Validation Error", "Supplier is required", "warning"); return false; }
        if (!String(formData.unit_id || "").trim()) { showToast("Validation Error", "Unit of measure is required", "warning"); return false; }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) { 
            showToast("Permission Denied", "You don't have permission to add products", "error"); 
            return; 
        }
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) { 
            showToast("Permission Denied", "You don't have permission to update products", "error"); 
            return; 
        }
        if (postStatus === "DELETE" && !hasPermission('DELETE')) { 
            showToast("Permission Denied", "You don't have permission to delete products", "error"); 
            return; 
        }
        
        if (postStatus !== "DELETE") { 
            if (!validateForm()) return; 
        }
        
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        
        if (postStatus !== "DELETE") {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('stock', formData.stock);
            formDataToSend.append('status', formData.status);
            formDataToSend.append('cost', formData.cost);
            formDataToSend.append('selling', formData.selling);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('rating', formData.rating);
            formDataToSend.append('category', formData.category);
            formDataToSend.append('payment', formData.payment);
            formDataToSend.append('supplier', formData.supplier);
            formDataToSend.append('unit_id', formData.unit_id);
        }
        
        if (postStatus === "UPDATE" || postStatus === "DELETE") { 
            formDataToSend.append('updateId', updateId); 
        }
        
        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIProductsStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIProductsUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIProductsDelete, formDataToSend);
            }
            
            if (result && result.success) { 
                showToast("Success", result.message, "success"); 
                handleCloseAdd(); 
                handleCloseDelete(); 
                await fetchData(); 
                await fetchProductsStats(); 
            } else {
                showToast("Error", result?.message || "Operation failed", "error");
            }
        } catch (error) { 
            console.error("API Error:", error); 
            showToast("Error", error?.message || "An error occurred", "error");
        }
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, product) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedProductForAction(product);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedProductForAction(null);
    };

    const renderGridView = () => {
        if (viewMode !== 'grid') return null;
        const currentData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        if (currentData.length === 0) {
            return (<Box sx={{ textAlign: 'center', py: 8 }}><Store sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} /><Typography variant="h6" color="text.secondary">No products found</Typography></Box>);
        }
        return (
            <Grid container spacing={2} sx={{ p: 2 }}>
                {currentData.map((record) => {
                    const unitName = pageState.unitsData.find(u => u.id === record.unit_id)?.name || 'N/A';
                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 6 } }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}><LocalOffer /></Avatar>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label={record.status == 1 ? 'Active' : 'Inactive'} color={record.status == 1 ? 'success' : 'error'} size="small" />
                                            <IconButton 
                                                size="small" 
                                                sx={{ color: theme.palette.action.main }}
                                                onClick={(e) => handleActionMenuOpen(e, record)}
                                                aria-label="actions"
                                            >
                                                <MoreVert />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Typography variant="h6" component="div" gutterBottom>{record.name}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        <Chip label={formatCurrency(record.selling)} color="primary" variant="outlined" size="small" />
                                        <Chip label={`Stock: ${formatNumberWithComma(record.stock)}`} color={record.stock > 10 ? 'success' : record.stock > 0 ? 'warning' : 'error'} variant="outlined" size="small" />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Chip icon={<Category />} label={getCategoriesNameById(record.category, pageState.categoriesData)} color="secondary" variant="outlined" size="small" />
                                        <Chip icon={<Scale />} label={unitName} color="info" variant="outlined" size="small" />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading Products Data..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Products Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Products</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : formatNumberWithComma(pageState.productsStats.total_products || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 48, height: 48 }}>
                                <Store sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Active Products</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : formatNumberWithComma(pageState.productsStats.active_products || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 48, height: 48 }}>
                                <CheckCircle sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Stock</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : formatNumberWithComma(pageState.productsStats.total_stock || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.info.light, width: 48, height: 48 }}>
                                <Inventory2 sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Inventory Value</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : formatCurrency(pageState.productsStats.total_inventory_value || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 48, height: 48 }}>
                                <AttachMoney sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Low Stock</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: (pageState.productsStats.low_stock_count || 0) > 0 ? theme.palette.warning.main : theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : formatNumberWithComma(pageState.productsStats.low_stock_count || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 48, height: 48 }}>
                                <Warning sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Avg. Price</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : formatCurrency(pageState.productsStats.average_price || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.secondary.light, width: 48, height: 48 }}>
                                <TrendingUp sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Products Management ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Add New Product
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                View Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => { fetchData(); fetchProductsStats(); }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list"><ViewList /></ToggleButton>
                            <ToggleButton value="grid"><ViewModule /></ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.category || filters.status || filters.unit_id || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.category && (
                            <Chip
                                label={`Category: ${getCategoriesNameById(filters.category, pageState.categoriesData)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, category: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.unit_id && (
                            <Chip
                                label={`Unit: ${pageState.unitsData.find(u => u.id === parseInt(filters.unit_id))?.name || 'N/A'}`}
                                onDelete={() => setFilters(prev => ({ ...prev, unit_id: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusById(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedProductForAction && handleOpenView(selectedProductForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedProductForAction && handlePrint(selectedProductForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('EDIT') && (
                    <MenuItem onClick={() => selectedProductForAction && handleOpenEdit(selectedProductForAction)}>
                        <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Edit</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('DELETE') && (
                    <MenuItem onClick={() => selectedProductForAction && handleOpenDelete(selectedProductForAction)}>
                        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* List View */}
                {viewMode === 'list' && (
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Unit</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="left">Cost</TableCell>
                                        <TableCell align="left">Selling</TableCell>
                                        <TableCell align="left">Profit</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell align="center">Stock</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product, index) => {
                                        const unitName = pageState.unitsData.find(u => u.id === product.unit_id)?.name || 'N/A';
                                        return (
                                            <TableRow
                                                key={product.id || index}
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        label={page * rowsPerPage + index + 1}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                        {formatCreatedAtDate(product.created_at)}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'medium' }}>{product.name}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={<Scale />}
                                                        label={unitName}
                                                        color="info"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {product.status == 1 ? 
                                                        <Chip icon={<CheckCircle />} label="Active" size="small" color="success" /> : 
                                                        <Chip icon={<Cancel />} label="Inactive" size="small" color="error" />
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={formatCurrency(product.cost || 0)} color="success" variant="outlined" size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={formatCurrency(product.selling || 0)} color="primary" variant="outlined" size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={formatCurrency((product.selling || 0) - (product.cost || 0))} 
                                                        color={(product.selling || 0) - (product.cost || 0) >= 0 ? 'error' : 'default'} 
                                                        variant="outlined" 
                                                        size="small" 
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={product.category_name} 
                                                        icon={<Category fontSize="small" />} 
                                                        size="small" 
                                                        color="secondary" 
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={formatNumberWithComma(product.stock || 0)} 
                                                        icon={<Storage fontSize="small" />} 
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: product.stock > 10 ? 'success.light' : product.stock > 0 ? 'warning.light' : 'error.light', 
                                                            color: 'white' 
                                                        }} 
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Actions">
                                                        <IconButton
                                                            sx={{ color: theme.palette.action.main }}
                                                            onClick={(e) => handleActionMenuOpen(e, product)}
                                                            size="small"
                                                        >
                                                            <MoreVert />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>

                                {/* Footer with Totals */}
                                {filteredData.length > 0 && (
                                    <TableBody>
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.primary.light + '20',
                                            borderTop: '3px solid',
                                            borderTopColor: theme.palette.primary.main,
                                            '& td': {
                                                fontWeight: 'bold',
                                                py: 2.5
                                            }
                                        }}>
                                            <TableCell colSpan={2} align="right">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Totals ({filteredTotals.totalCount} products):
                                                </Typography>
                                            </TableCell>
                                            <TableCell></TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label={`${filteredTotals.activeProducts} Active`}
                                                        size="small"
                                                        color="success"
                                                    />
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label={`${filteredTotals.inactiveProducts} Inactive`}
                                                        size="small"
                                                        color="error"
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                        {formatCurrency(filteredTotals.totalCost)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg: {formatCurrency(filteredTotals.avgCost)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                        {formatCurrency(filteredTotals.totalSelling)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg: {formatCurrency(filteredTotals.avgPrice)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Chip
                                                    label={formatCurrency(filteredTotals.totalProfit)}
                                                    color={filteredTotals.totalProfit >= 0 ? 'error' : 'default'}
                                                    size="small"
                                                    icon={filteredTotals.totalProfit >= 0 ? <TrendingUp /> : <TrendingDown />}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<Category />}
                                                    label={`${filteredTotals.uniqueCategories} Categories`}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                                    <Chip
                                                        label={`${formatNumberWithComma(filteredTotals.totalStock)} Total`}
                                                        size="small"
                                                        color="primary"
                                                        icon={<Inventory />}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Chip
                                                            label={`${filteredTotals.inStockProducts} In Stock`}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            label={`${filteredTotals.outOfStockProducts} Out`}
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={formatCurrency(filteredTotals.totalInventoryValue)}
                                                    size="small"
                                                    color="warning"
                                                    icon={<AttachMoney />}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && renderGridView()}

                {filteredData.length === 0 && viewMode === 'list' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 8,
                        color: 'text.secondary'
                    }}>
                        <Store sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">No products found</Typography>
                        <Typography variant="body2">Try adjusting your search query</Typography>
                    </Box>
                )}
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Products
                        </Button>

                        <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                                Filter by Loan Status
                            </Typography>

                            <FormControl fullWidth>
                                <InputLabel>Loan Status</InputLabel>
                                <Select
                                    name="loan_filter"
                                    value={filters.loan_filter}
                                    onChange={handleFilterSelectChange}
                                    label="Loan Status"
                                >
                                    <MenuItem value="">All Products</MenuItem>
                                    <MenuItem value="with_loans">With Loans Only</MenuItem>
                                    <MenuItem value="without_loans">Without Loans Only</MenuItem>
                                </Select>
                            </FormControl>
                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                name="category"
                                value={filters.category}
                                onChange={handleFilterSelectChange}
                                label="Category"
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {pageState.categoriesData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Unit of Measure</InputLabel>
                            <Select
                                name="unit_id"
                                value={filters.unit_id}
                                onChange={handleFilterSelectChange}
                                label="Unit of Measure"
                            >
                                <MenuItem value="">All Units</MenuItem>
                                {pageState.unitsData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name} ({item.code})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Product Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Prices
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Cost Price"
                                    name="cost"
                                    value={filters.cost}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Selling Price"
                                    name="selling"
                                    value={filters.selling}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                        </Grid>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterSelectChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Product" : "Edit Product"}</b>
                    {postStatus === "UPDATE" && selectedRecord && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Editing: {selectedRecord.name}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit}>
                        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 3 }}>
                            <Tab label="Basic Info" />
                            <Tab label="Payment" />
                            <Tab label="Pricing" />
                            <Tab label="Settings" />
                        </Tabs>

                        {/* Tab 0: Basic Info */}
                        {activeTab === 0 && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                                    Basic Information
                                </Typography>

                                <TextField
                                    fullWidth
                                    label="Product Name *"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocalOffer />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Description *"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Description />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Category Selection with Autocomplete */}
                                <Autocomplete
                                    id="category-select"
                                    options={pageState.categoriesData}
                                    loading={loading}
                                    value={pageState.categoriesData.find(c => c.id === formData.category) || null}
                                    onChange={handleCategorySelect}
                                    inputValue={categorySearchInput}
                                    onInputChange={(event, newInputValue) => {
                                        setCategorySearchInput(newInputValue);
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">{option.name}</Typography>
                                                {option.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Category *"
                                            placeholder="Search category..."
                                            required
                                            error={errors.category}
                                            helperText={errors.category ? "Category is required" : ""}
                                            sx={{ mb: 2 }}
                                        />
                                    )}
                                />

                                {/* Supplier Selection with Autocomplete */}
                                <Autocomplete
                                    id="supplier-select"
                                    options={pageState.suppliersData}
                                    loading={loading}
                                    value={pageState.suppliersData.find(s => s.id === formData.supplier) || null}
                                    onChange={handleSupplierSelect}
                                    inputValue={supplierSearchInput}
                                    onInputChange={(event, newInputValue) => {
                                        setSupplierSearchInput(newInputValue);
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">{option.name}</Typography>
                                                {option.phone && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Phone: {option.phone}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Supplier *"
                                            placeholder="Search supplier..."
                                            required
                                            error={errors.supplier}
                                            helperText={errors.supplier ? "Supplier is required" : ""}
                                            sx={{ mb: 2 }}
                                        />
                                    )}
                                />

                                {/* Unit Selection with Autocomplete */}
                                <Autocomplete
                                    id="unit-select"
                                    options={pageState.unitsData}
                                    loading={loading}
                                    value={pageState.unitsData.find(u => u.id === formData.unit_id) || null}
                                    onChange={handleUnitSelect}
                                    inputValue={unitSearchInput}
                                    onInputChange={(event, newInputValue) => {
                                        setUnitSearchInput(newInputValue);
                                    }}
                                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">
                                                    {option.name} ({option.code})
                                                </Typography>
                                                {option.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Unit of Measure *"
                                            placeholder="Search unit..."
                                            required
                                            error={errors.unit_id}
                                            helperText={errors.unit_id ? "Unit of measure is required" : "Select the unit for this product"}
                                            sx={{ mb: 2 }}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <Scale />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        {/* Tab 1: Payment */}
                        {activeTab === 1 && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                                    Payment Information
                                </Typography>

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Payment Method *</InputLabel>
                                    <Select
                                        name="payment"
                                        value={formData.payment}
                                        onChange={handleInputChange}
                                        label="Payment Method *"
                                    >
                                        <MenuItem value="Cash">Cash</MenuItem>
                                        <MenuItem value="Loan">Loan</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        {/* Tab 2: Pricing */}
                        {activeTab === 2 && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                                    Pricing Information
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Cost Price *"
                                            name="cost"
                                            type="number"
                                            value={formData.cost}
                                            onChange={handleInputChange}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoney />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Selling Price *"
                                            name="selling"
                                            type="number"
                                            value={formData.selling}
                                            onChange={handleInputChange}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoney />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Stock *"
                                            name="stock"
                                            type="number"
                                            value={formData.stock}
                                            onChange={handleInputChange}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Inventory2 />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Rating *"
                                            name="rating"
                                            type="number"
                                            value={formData.rating}
                                            onChange={handleInputChange}
                                            required
                                            inputProps={{ min: 0, max: 5, step: 0.5 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Assessment />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">/ 5</InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Tab 3: Settings */}
                        {activeTab === 3 && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                                    Product Settings
                                </Typography>

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Status *</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        label="Status *"
                                    >
                                        {statusData.map((item, index) => (
                                            <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Box>
                            {activeTab > 0 && (
                                <Button color="primary" onClick={handlePrevTab} variant="outlined" sx={{ mr: 1 }}>
                                    Previous
                                </Button>
                            )}
                        </Box>
                        <Box>
                            <Button color="error" onClick={handleCloseAdd} variant="contained" sx={{ mr: 1 }}>
                                Cancel
                            </Button>
                            {activeTab < 3 ? (
                                <Button color="primary" onClick={handleNextTab} variant="contained">
                                    Next
                                </Button>
                            ) : (
                                <Button type="submit" color="primary" variant="contained" onClick={handleSubmit}>
                                    {postStatus === "ADD" ? "Add Product" : "Update Product"}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Product Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Tabs value={viewActiveTab} onChange={(e, v) => setViewActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Info />} iconPosition="start" />
                                <Tab label="Pricing" icon={<AttachMoney />} iconPosition="start" />
                            </Tabs>

                            {viewActiveTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 64, height: 64 }}>
                                            <LocalOffer sx={{ fontSize: 36 }} />
                                        </Avatar>
                                    </Box>
                                    <Typography variant="h6" align="center" color="primary" gutterBottom>
                                        {selectedRecord.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                                        {selectedRecord.description}
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Category:</b> {selectedRecord.category_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Unit:</b> {pageState.unitsData.find(u => u.id === selectedRecord.unit_id)?.name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Status:</b> {selectedRecord.status == 1 ? 
                                                    <Chip icon={<CheckCircle />} label="Active" size="small" color="success" /> : 
                                                    <Chip icon={<Cancel />} label="Inactive" size="small" color="error" />
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Supplier:</b> {selectedRecord.supplier_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Payment:</b> <Chip label={selectedRecord.payment || 'N/A'} size="small" color="info" />
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Stock Status:</b> {
                                                    selectedRecord.stock > 10 ? 
                                                        <Chip icon={<CheckCircle />} label="In Stock" size="small" color="success" /> :
                                                        selectedRecord.stock > 0 ? 
                                                            <Chip icon={<Warning />} label="Low Stock" size="small" color="warning" /> :
                                                            <Chip icon={<Cancel />} label="Out of Stock" size="small" color="error" />
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {viewActiveTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Cost Price</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                    {formatCurrency(selectedRecord.cost)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Selling Price</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    {formatCurrency(selectedRecord.selling)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Profit Margin</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                    {formatCurrency((selectedRecord.selling || 0) - (selectedRecord.cost || 0))}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Stock</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {formatNumberWithComma(selectedRecord.stock)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Rating</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                                    {selectedRecord.rating || 'N/A'} Stars
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* System Information */}
                            <SystemInformation
                                createdBy={selectedRecord.created_by}
                                updatedBy={selectedRecord.updated_by}
                                createdAt={selectedRecord.created_at}
                                updatedAt={selectedRecord.updated_at}
                                title="Product System Information"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Product</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this product?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name}
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    label={getCategoriesNameById(selectedRecord.category, pageState.categoriesData)}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    label={formatCurrency(selectedRecord.selling)}
                                    color="error"
                                    variant="outlined"
                                    size="small"
                                />
                                <Chip
                                    label={`Stock: ${formatNumberWithComma(selectedRecord.stock)}`}
                                    color={selectedRecord.stock > 0 ? 'success' : 'error'}
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                        Warning: This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Products;
